import React from 'react';
import { format } from 'date-fns';

/**
 * EventPill - Compact event display for calendar views
 *
 * Color System (per plan):
 * - Background = Category (dominant visual)
 *   - External: Blue (customer appointments)
 *   - Internal: Purple (team meetings)
 *   - Personal: Green (private tasks)
 * - Dot = Company indicator (only for multi-company users)
 *   - Shows colored dot in top-left corner
 *   - Hover reveals company name tooltip
 *
 * Visual Indicators for RSVP Status:
 * - Pending (null + requiresResponse) or Tentative: Grey/dashed border
 * - Accepted: Normal solid display
 * - Declined: Should be filtered out by backend
 */

// Category color classes
const CATEGORY_COLORS = {
  external: {
    bg: 'bg-blue-50',
    border: 'border-l-blue-500',
    text: 'text-blue-900',
    time: 'text-blue-600'
  },
  internal: {
    bg: 'bg-purple-50',
    border: 'border-l-purple-500',
    text: 'text-purple-900',
    time: 'text-purple-600'
  },
  personal: {
    bg: 'bg-green-50',
    border: 'border-l-green-500',
    text: 'text-green-900',
    time: 'text-green-600'
  },
  schedule: {
    bg: 'bg-orange-50',
    border: 'border-l-orange-500',
    text: 'text-orange-900',
    time: 'text-orange-600'
  }
};

// Default colors for unknown category
const DEFAULT_COLORS = {
  bg: 'bg-gray-50',
  border: 'border-l-gray-400',
  text: 'text-gray-900',
  time: 'text-gray-600'
};

// Pending/Tentative response colors (needs attention indicator)
const PENDING_RESPONSE_COLORS = {
  bg: 'bg-gray-100',
  border: 'border-l-gray-400',
  text: 'text-gray-600',
  time: 'text-gray-500'
};

/**
 * Check if event needs attention (awaiting RSVP response)
 * Shows grey/dashed styling for:
 * - Pending: requiresResponse=true AND responseStatus=null
 * - Tentative: responseStatus='tentative'
 */
const eventNeedsAttention = (event) => {
  // Check if current user needs to respond
  const requiresResponse = event.currentUserRequiresResponse;
  const responseStatus = event.currentUserResponseStatus;

  // No response needed or already accepted
  if (!requiresResponse || responseStatus === 'accepted') {
    return false;
  }

  // Pending (null) or Tentative - needs attention
  return responseStatus === null || responseStatus === 'tentative';
};

/**
 * Check if task is closed (completed or cancelled)
 * Used to show subtle visual indicator on calendar view
 */
const isTaskClosed = (event) => {
  return event.eventType === 'task' && ['completed', 'cancelled'].includes(event.status);
};

/**
 * EventPill Component
 *
 * @param {Object} event - Event data
 * @param {string} event.id - Event ID
 * @param {string} event.title - Event title
 * @param {string} event.category - 'external' | 'internal' | 'personal'
 * @param {string} event.startTime - ISO datetime string
 * @param {string} event.endTime - ISO datetime string
 * @param {boolean} event.allDay - Is this an all-day event
 * @param {string} event.companyColor - Company calendar color (for multi-company)
 * @param {string} event.companyName - Company name (for tooltip)
 * @param {function} onClick - Click handler
 * @param {string} size - 'sm' | 'md' | 'lg' - Display size
 * @param {boolean} showTime - Whether to show time
 * @param {string} contentAlign - 'top' | 'center' - Vertical alignment of content
 */
const EventPill = ({
  event,
  onClick,
  size = 'sm',
  showTime = true,
  contentAlign = 'top'
}) => {
  // Check if event needs attention (pending/tentative RSVP)
  const needsAttention = eventNeedsAttention(event);

  // Check if task is closed (completed/cancelled)
  const isClosed = isTaskClosed(event);

  // Use grey colors for pending events, otherwise use category colors
  const colors = needsAttention
    ? PENDING_RESPONSE_COLORS
    : (CATEGORY_COLORS[event.category] || DEFAULT_COLORS);

  // Format time display
  const getTimeDisplay = () => {
    if (event.allDay) return 'All day';
    if (!event.startTime) return '';

    try {
      const startDate = new Date(event.startTime);
      return format(startDate, 'h:mm a');
    } catch {
      return '';
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'text-xs py-0.5 px-1.5',
    md: 'text-sm py-1 px-2',
    lg: 'text-sm py-1.5 px-2.5'
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick(event);
  };

  // Build title with status indicator
  const titleText = needsAttention
    ? `${event.title} (Awaiting your response)`
    : isClosed
      ? `${event.title} (Completed)`
      : `${event.title}${event.allDay ? ' (All day)' : ''}`;

  // Content alignment classes
  const alignmentClasses = {
    top: 'justify-start',
    center: 'justify-center'
  };

  return (
    <button
      onClick={handleClick}
      className={`
        w-full h-full text-left rounded overflow-hidden flex flex-col
        border-l-4 ${colors.border} ${colors.bg}
        hover:shadow-sm transition-shadow cursor-pointer
        ${sizeClasses[size]}
        ${alignmentClasses[contentAlign] || 'justify-start'}
        ${needsAttention ? 'border-dashed opacity-75' : ''}
        ${isClosed ? 'opacity-50' : ''}
      `}
      title={titleText}
    >
      <div className="flex items-start gap-1 min-w-0">
        {/* Company dot (only shown for multi-company users) */}
        {event.companyColor && (
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: event.companyColor }}
            title={event.companyName || 'Company'}
          />
        )}

        {/* Time (optional) */}
        {showTime && getTimeDisplay() && (
          <span className={`flex-shrink-0 ${colors.time} font-medium`}>
            {getTimeDisplay()}
          </span>
        )}

        {/* Title */}
        <span className={`truncate ${colors.text} ${isClosed ? 'line-through' : ''}`}>
          {event.title}
        </span>
      </div>
    </button>
  );
};

/**
 * EventPillCompact - Even smaller version for dense calendar views
 */
export const EventPillCompact = ({ event, onClick, showDot = false }) => {
  // Check if event needs attention (pending/tentative RSVP)
  const needsAttention = eventNeedsAttention(event);

  // Check if task is closed (completed/cancelled)
  const isClosed = isTaskClosed(event);

  // Use grey colors for pending events, otherwise use category colors
  const colors = needsAttention
    ? PENDING_RESPONSE_COLORS
    : (CATEGORY_COLORS[event.category] || DEFAULT_COLORS);

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick(event);
  };

  // Build title with status indicator
  const titleText = needsAttention
    ? `${event.title} (Awaiting your response)`
    : isClosed
      ? `${event.title} (Completed)`
      : event.title;

  return (
    <button
      onClick={handleClick}
      className={`
        w-full text-left text-xs py-0.5 px-1 rounded truncate
        ${colors.bg} hover:shadow-sm transition-shadow cursor-pointer
        ${needsAttention ? 'border border-dashed border-gray-400 opacity-75' : ''}
        ${isClosed ? 'opacity-50' : ''}
      `}
      title={titleText}
    >
      <div className="flex items-center gap-1">
        {showDot && (
          <span
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.border.replace('border-l-', 'bg-')}`}
          />
        )}
        <span className={`truncate ${colors.text} ${isClosed ? 'line-through' : ''}`}>
          {event.title}
        </span>
      </div>
    </button>
  );
};

/**
 * EventPillStack - Shows overflow indicator when there are more events
 */
export const EventPillStack = ({ count, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="text-xs text-gray-500 hover:text-gray-700 font-medium py-0.5 px-1"
    >
      +{count} more
    </button>
  );
};

export default EventPill;
